import { Link, Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { ROTULO_PERFIL_OPERADOR } from '@construtora/contracts';
import { api } from '@/shared/lib/api';
import { cls } from '@/shared/lib/classes';
import { encerrarSessaoOperadorLocal, useSessaoOperador } from '@/shared/lib/sessao-operador';
import { Botao } from '@/shared/ui/Botao';
import { Marca, NomeDaMarca } from '@/shared/ui/Marca';
import { queryClient } from './query-client';

/**
 * Seções do console.
 *
 * Fora do componente e `as const`: além de não ser recriado a cada render, o
 * `to` do `Link` é tipado contra a árvore de rotas — um caminho digitado errado
 * aqui vira erro de compilação, não link morto descoberto em produção.
 *
 * Sem filtro por permissão, ao contrário do menu do cliente: os dois perfis de
 * operador enxergam as duas telas. SUPORTE entra em NCM e vê o estado do
 * catálogo; o que ele não tem é o botão de atualizar, e essa checagem fica na
 * própria tela.
 */
const SECOES = [
  { para: '/operacao/contas', rotulo: 'Contas' },
  { para: '/operacao/ncms', rotulo: 'Catálogo de NCM' },
  { para: '/operacao/seguranca', rotulo: 'Segurança' },
] as const;

/** Com o segundo fator pendente, o console é só a tela de segurança. */
const CAMINHO_SEGURANCA = '/operacao/seguranca';

/**
 * Moldura do console de operação.
 *
 * **Tema invertido de propósito.** O plano do cliente é papel claro; este é
 * grafite escuro, com uma faixa fixa no topo dizendo o que é. Quem opera o
 * sistema costuma ter as duas coisas abertas ao mesmo tempo, e confundir as
 * janelas aqui significa suspender a conta errada. A diferença precisa ser
 * perceptível antes da leitura, não depois.
 */
export function OperacaoShell(): React.ReactElement {
  const sessao = useSessaoOperador();
  const navigate = useNavigate();
  const caminho = useRouterState({ select: (estado) => estado.location.pathname });
  const [saindo, setSaindo] = useState(false);

  /*
    A sessão caiu sem o operador pedir — sai da tela. Mesma metade reativa da
    guarda de rota que o `AppShell` tem, e pelo mesmo motivo: `beforeLoad` só
    roda ao navegar, e quem perde a sessão no meio de uma consulta fica parado
    numa tela que não busca mais nada, com o nome de quem era no cabeçalho.

    O cache do console vai junto: ele guarda a lista de contas de clientes, e é
    o tipo de resposta que não deve sobreviver à troca de operador.
  */
  useEffect(() => {
    if (sessao) return;

    queryClient.clear();
    void navigate({ to: '/operacao/entrar', replace: true });
  }, [sessao, navigate]);

  /*
    Segundo fator obrigatório: operador sem ele só alcança a tela de segurança.
    É guarda de interface — a API continua respondendo às rotas, e o que
    protege de verdade é o login exigir o código assim que o fator estiver
    ativo. O que esta guarda faz é não deixar "depois eu ativo" virar nunca.
  */
  useEffect(() => {
    if (!sessao?.mfaPendente || caminho === CAMINHO_SEGURANCA) return;
    void navigate({ to: CAMINHO_SEGURANCA, replace: true });
  }, [sessao, caminho, navigate]);

  async function sair(): Promise<void> {
    setSaindo(true);
    try {
      await api.post('/api/v1/operacao/auth/logout', {});
    } finally {
      encerrarSessaoOperadorLocal();
      queryClient.clear();
      await navigate({ to: '/operacao/entrar', replace: true });
    }
  }

  return (
    <div className="min-h-screen bg-grafite-950 text-papel-200">
      {/* Faixa de identificação: some da tela só quando a aba fecha. */}
      <div className="flex items-center justify-center gap-2 bg-zarcao-600 px-4 py-1.5 text-center">
        <span className="cota text-white">
          Console de operação · você está administrando contas de clientes
        </span>
      </div>

      <header className="border-b border-grafite-800">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-4 px-4 py-4 sm:px-6 lg:px-10">
          <div className="flex items-center gap-3">
            <Marca className="h-7 w-7 text-zarcao-500" classeDaLinha="text-papel-50" />
            <div className="leading-none">
              <NomeDaMarca tom="escuro" className="block text-lg" />
              <p className="cota mt-1 text-grafite-400">Operação</p>
            </div>
          </div>

          <div className="ml-auto flex items-center gap-4">
            {sessao ? (
              <div className="text-right leading-tight">
                <p className="text-sm font-medium text-papel-100">{sessao.nome}</p>
                <p className="cota text-grafite-400">{ROTULO_PERFIL_OPERADOR[sessao.perfil]}</p>
              </div>
            ) : null}

            <Botao
              tamanho="compacto"
              onClick={() => void sair()}
              disabled={saindo}
              className="border-grafite-600 bg-transparent text-papel-200 shadow-none hover:border-papel-300 hover:bg-grafite-800"
            >
              Sair
            </Botao>
          </div>
        </div>
      </header>

      {/*
        A navegação nasce com a segunda tela do console, não antes: uma barra
        com um item só é moldura sem função. Os dois destinos são de naturezas
        diferentes — contas de clientes e catálogos do sistema —, e é isso que
        o menu precisa deixar claro.
      */}
      <nav aria-label="Seções do console" className="border-b border-grafite-800 bg-grafite-900/60">
        <div className="mx-auto flex max-w-6xl gap-1 px-4 sm:px-6 lg:px-10">
          {SECOES.map((secao) => {
            const ativa = caminho.startsWith(secao.para);
            // Pendente: os outros destinos ficam visíveis (para a pessoa saber
            // o que existe), mas o clique volta para a segurança.
            if (sessao?.mfaPendente && secao.para !== CAMINHO_SEGURANCA) {
              return (
                <span
                  key={secao.para}
                  aria-disabled="true"
                  className="flex min-h-toque cursor-not-allowed items-center border-b-2 border-b-transparent px-3 text-sm font-medium text-grafite-600"
                >
                  {secao.rotulo}
                </span>
              );
            }

            return (
              <Link
                key={secao.para}
                to={secao.para}
                className={cls(
                  'flex min-h-toque items-center border-b-2 px-3 text-sm font-medium transition-colors',
                  ativa
                    ? 'border-b-zarcao-500 text-papel-50'
                    : 'border-b-transparent text-grafite-300 hover:text-papel-100',
                )}
              >
                {secao.rotulo}
              </Link>
            );
          })}
        </div>
      </nav>

      <main className="mx-auto w-full max-w-6xl px-4 py-8 sm:px-6 lg:px-10">
        <Outlet />
      </main>
    </div>
  );
}
